import { batch } from 'react-redux';

import { aiProcessRunsApi } from 'app/services/api';
import _ from 'lodash';

import { LOAN_APPLICATION_ACTION_NAME_PREFIX } from 'constants/loanApplication';
import { createNamedActions } from 'utils/namedActionTypesProxy';

import { getUuid } from 'store/auth/selectors';

import { selectPendingChangesAiRunsByExecutionId } from '../selectors/pendingChangesAuthorsSelection.selectors';

import { remountActiveTabAction } from './controlUI.actions';
import { loadCustomFieldValuesAction } from './customFieldValues.actions';
import { getLoanApplicationHeaderViewAction } from './headerView.actions';
import { getLoanApplicationCommonDataByApplicationIdAction } from './loanApplication.actions';

export const PENDING_CHANGES_AUTHORS_SELECTION_ACTION_TYPE = createNamedActions(
  `${LOAN_APPLICATION_ACTION_NAME_PREFIX}/pendingChangesAuthorsSelection`,
  {
    OPEN_SELECTION: 'OPEN_SELECTION',
    CLOSE_SELECTION: 'CLOSE_SELECTION',
    SET_SELECTION: 'SET_SELECTION',
    SET_AI_RUNS: 'SET_AI_RUNS',
  },
);

export const openPendingChangesAuthorsSelectionAction =
  ({ applicationId }) =>
  (dispatch, getState) =>
    dispatch({
      type: PENDING_CHANGES_AUTHORS_SELECTION_ACTION_TYPE.OPEN_SELECTION,
      payload: { applicationId, defaultAuthorKeys: _.compact([getUuid(getState())]) },
    });

export const closePendingChangesAuthorsSelectionAction = ({ applicationId }) => ({
  type: PENDING_CHANGES_AUTHORS_SELECTION_ACTION_TYPE.CLOSE_SELECTION,
  payload: { applicationId },
});

const setPendingChangesAuthorsSelectionAction = ({ applicationId, authorKeys }) => ({
  type: PENDING_CHANGES_AUTHORS_SELECTION_ACTION_TYPE.SET_SELECTION,
  payload: { applicationId, authorKeys },
});

const reloadLoanForPendingChangesSelectionAction =
  ({ applicationId }) =>
  dispatch =>
    batch(() => {
      dispatch(remountActiveTabAction({ triggeredByPendingChanges: true }));
      dispatch(getLoanApplicationHeaderViewAction({ applicationId }));
      dispatch(getLoanApplicationCommonDataByApplicationIdAction({ applicationId }));
      dispatch(loadCustomFieldValuesAction({ applicationId }));
    });

/**
 * Stores whose pending changes the loan shows and reloads the loan so every tab, the header and all
 * calculations are re-read under the new X-Skynet-Pending-Changes-Authors header.
 */
export const changePendingChangesAuthorsSelectionAction =
  ({ applicationId, authorKeys }) =>
  dispatch => {
    dispatch(setPendingChangesAuthorsSelectionAction({ applicationId, authorKeys }));
    dispatch(reloadLoanForPendingChangesSelectionAction({ applicationId }));
  };

const fetchAiRun = executionId =>
  aiProcessRunsApi
    .getRunDetail({ executionId })
    .then(({ data }) => [executionId, data ?? null])
    .catch(() => [executionId, null]);

// Run details only refine the "started by" label; a missing run or a 403 falls back to the change author.
export const loadPendingChangesAiRunsAction =
  ({ executionIds }) =>
  async (dispatch, getState) => {
    const loadedRuns = selectPendingChangesAiRunsByExecutionId(getState());
    const missingExecutionIds = _.uniq(_.compact(executionIds)).filter(
      executionId => !_.has(loadedRuns, executionId),
    );

    if (!missingExecutionIds.length) {
      return null;
    }

    const runEntries = await Promise.all(missingExecutionIds.map(fetchAiRun));

    return dispatch({
      type: PENDING_CHANGES_AUTHORS_SELECTION_ACTION_TYPE.SET_AI_RUNS,
      payload: _.fromPairs(runEntries),
    });
  };
